import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  DollarSign,
  Eye,
  EyeOff,
  Lock,
  Package,
  PieChart,
  ScanLine,
  Moon,
  Sun,
  Truck,
  User,
  Users,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../../auth/AuthContext';

const orchestrationNodes = [
  {
    id: 'finance',
    title: 'Finance',
    subtitle: 'Ledger sync',
    x: 280,
    y: 150,
    icon: DollarSign,
    accent: 'from-emerald-300 to-emerald-500',
  },
  {
    id: 'supply',
    title: 'Supply',
    subtitle: 'Smart ATP',
    x: 330,
    y: 510,
    icon: Package,
    accent: 'from-cyan-300 to-sky-500',
  },
  {
    id: 'workforce',
    title: 'Workforce',
    subtitle: 'Shift AI',
    x: 720,
    y: 150,
    icon: Users,
    accent: 'from-violet-300 to-indigo-500',
  },
  {
    id: 'logistics',
    title: 'Logistics',
    subtitle: 'Route mesh',
    x: 760,
    y: 335,
    icon: Truck,
    accent: 'from-amber-300 to-orange-500',
  },
  {
    id: 'insights',
    title: 'Insights',
    subtitle: 'Live BI',
    x: 680,
    y: 510,
    icon: PieChart,
    accent: 'from-blue-300 to-cyan-500',
  },
  {
    id: 'ops',
    title: 'Operations',
    subtitle: 'Execution scan',
    x: 250,
    y: 335,
    icon: ScanLine,
    accent: 'from-teal-300 to-emerald-500',
  },
];

const orchestrationCenter = { x: 500, y: 320 };
const orchestratorWidth = 1000;
const orchestratorHeight = 640;
const coreLinkRadius = 72;
const coreSizeClass = 'h-36 w-36';

const nodeLinks = orchestrationNodes.map((node) => {
  const deltaX = node.x - orchestrationCenter.x;
  const deltaY = node.y - orchestrationCenter.y;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const unitX = deltaX / distance;
  const unitY = deltaY / distance;

  const startX = orchestrationCenter.x + unitX * coreLinkRadius;
  const startY = orchestrationCenter.y + unitY * coreLinkRadius;

  return {
    ...node,
    startX,
    startY,
    motionPath: `M${startX},${startY} L${node.x},${node.y}`,
  };
});

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const redirectTarget = location.state?.from && location.state.from !== '/login'
    ? location.state.from
    : '/';

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    setError('');

    const result = login({ identifier, password });

    if (!result.ok) {
      setError(result.error);
      setIsSubmitting(false);
      return;
    }

    navigate(redirectTarget, { replace: true });
  };

  return (
    <div className={`auth-login-shell min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'text-white' : 'text-slate-950'
    }`}>
      <div className="auth-login-grid relative min-h-screen overflow-hidden">
        <div className="auth-scanline pointer-events-none absolute inset-x-0 top-0 h-40 opacity-80" />
        <div className="pointer-events-none absolute inset-0">
          <motion.div
            animate={{ x: [0, 28, 0], y: [0, -22, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            className="auth-orb absolute -left-20 top-16 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 24, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
            className="auth-orb absolute right-0 top-0 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl"
          />
          <motion.div
            animate={{ x: [0, 16, 0], y: [0, -18, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            className="auth-orb absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-emerald-400/[0.15] blur-3xl"
          />
        </div>

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-6 sm:px-6 lg:flex-row lg:items-stretch lg:px-8">
          <div className="relative flex flex-1 items-stretch py-4 lg:py-8">
            <div className="relative w-full overflow-hidden rounded-[2rem] border border-white/[0.12] bg-slate-950/[0.55] shadow-[0_40px_120px_rgba(2,12,27,0.35)] backdrop-blur-2xl">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.22),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(45,212,191,0.2),_transparent_32%)]" />
              <div className="absolute inset-x-10 top-10 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />

              <div className="relative flex h-full flex-col gap-8 p-6 sm:p-8 lg:p-10">
                <div className="space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200/75">
                    LYNCS ERP INTELLIGENCE
                  </p>
                  <h1 className="font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
                    Unified command center for enterprise operations.
                  </h1>
                </div>

                <div className="erp-orchestrator relative aspect-[25/16] w-full overflow-hidden rounded-[1.8rem] border border-white/[0.12] bg-slate-950/[0.45]">
                  <svg
                    viewBox={`0 0 ${orchestratorWidth} ${orchestratorHeight}`}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {nodeLinks.map((node, index) => (
                      <g key={node.id}>
                        <line
                          x1={node.startX}
                          y1={node.startY}
                          x2={node.x}
                          y2={node.y}
                          className="erp-link-line"
                        />
                        <circle cx={node.startX} cy={node.startY} r="2.2" className="erp-link-anchor" />
                        <circle r="3.5" className="erp-link-pulse">
                          <animateMotion
                            dur="4.8s"
                            repeatCount="indefinite"
                            begin={`${index * 0.65}s`}
                            path={node.motionPath}
                          />
                        </circle>
                      </g>
                    ))}
                  </svg>

                  <div className="absolute inset-0">
                    {nodeLinks.map((node, index) => (
                      <div
                        key={node.id}
                        style={{
                          left: `${(node.x / orchestratorWidth) * 100}%`,
                          top: `${(node.y / orchestratorHeight) * 100}%`,
                        }}
                        className="absolute z-20 -translate-x-1/2 -translate-y-1/2"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 18 }}
                          animate={{ opacity: 1, y: [0, -6, 0] }}
                          transition={{
                            opacity: { duration: 0.45, delay: 0.1 * index },
                            y: {
                              duration: 4.6 + index * 0.25,
                              repeat: Infinity,
                              ease: 'easeInOut',
                              delay: 0.15 * index,
                            },
                          }}
                          className="w-36 rounded-2xl border border-white/10 bg-slate-900/[0.75] p-3 shadow-xl backdrop-blur"
                        >
                          <div className={`inline-flex rounded-xl bg-gradient-to-br p-2 ${node.accent}`}>
                            <node.icon className="h-4 w-4 text-slate-950" />
                          </div>
                          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-200/85">
                            {node.title}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-300/75">{node.subtitle}</p>
                        </motion.div>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      left: `${(orchestrationCenter.x / orchestratorWidth) * 100}%`,
                      top: `${(orchestrationCenter.y / orchestratorHeight) * 100}%`,
                    }}
                    className="absolute z-30 -translate-x-1/2 -translate-y-1/2"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.45, delay: 0.25 }}
                      className={`erp-core relative ${coreSizeClass}`}
                    >
                      <div className="erp-core-ring erp-core-ring--outer" />
                      <div className="erp-core-ring erp-core-ring--inner" />
                      <div className="absolute inset-5 rounded-full border border-cyan-300/45 bg-cyan-400/10 backdrop-blur">
                        <div className="flex h-full w-full flex-col items-center justify-center gap-1">
                          <Package className="h-5 w-5 text-cyan-200" />
                          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-100/90">
                            LYNCS CORE
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-center py-4 lg:w-[32rem] lg:py-8 lg:pl-8">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55 }}
              className={`w-full overflow-hidden rounded-[2rem] border shadow-[0_30px_90px_rgba(15,23,42,0.24)] backdrop-blur-2xl ${
                isDarkMode
                  ? 'border-white/10 bg-slate-950/[0.72]'
                  : 'border-white/60 bg-white/[0.78]'
              }`}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 via-sky-500 to-blue-600 shadow-lg shadow-cyan-500/30">
                      <Bot className="h-6 w-6 text-slate-950" />
                    </div>
                    <p className={`mt-4 text-sm font-semibold uppercase tracking-[0.24em] ${
                      isDarkMode ? 'text-cyan-200/75' : 'text-cyan-700'
                    }`}>
                      LYNCS Web
                    </p>
                    <h2 className={`font-display mt-2 text-3xl font-semibold ${
                      isDarkMode ? 'text-white' : 'text-slate-950'
                    }`}>
                      Welcome back
                    </h2>
                    <p className={`mt-3 text-sm leading-6 ${
                      isDarkMode ? 'text-slate-300/80' : 'text-slate-600'
                    }`}>
                      Sign in to continue to your ERP workspace.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={toggleTheme}
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl border transition-colors ${
                      isDarkMode
                        ? 'border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.12]'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                    aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>
                </div>

                <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${
                      isDarkMode ? 'text-slate-200' : 'text-slate-700'
                    }`} htmlFor="identifier">
                      Username or email
                    </label>
                    <div className={`flex items-center gap-3 rounded-[1.2rem] border px-4 py-3 transition-colors ${
                      isDarkMode
                        ? 'border-white/10 bg-slate-900/[0.55] focus-within:border-cyan-400/50'
                        : 'border-slate-200 bg-white/90 focus-within:border-cyan-500'
                    }`}>
                      <User className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                      <input
                        id="identifier"
                        type="text"
                        autoComplete="username"
                        value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        placeholder="Enter username or email"
                        className={`w-full border-none bg-transparent text-sm outline-none ${
                          isDarkMode
                            ? 'text-white placeholder:text-slate-500'
                            : 'text-slate-950 placeholder:text-slate-400'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${
                      isDarkMode ? 'text-slate-200' : 'text-slate-700'
                    }`} htmlFor="password">
                      Password
                    </label>
                    <div className={`flex items-center gap-3 rounded-[1.2rem] border px-4 py-3 transition-colors ${
                      isDarkMode
                        ? 'border-white/10 bg-slate-900/[0.55] focus-within:border-cyan-400/50'
                        : 'border-slate-200 bg-white/90 focus-within:border-cyan-500'
                    }`}>
                      <Lock className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Enter password"
                        className={`w-full border-none bg-transparent text-sm outline-none ${
                          isDarkMode
                            ? 'text-white placeholder:text-slate-500'
                            : 'text-slate-950 placeholder:text-slate-400'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className={`transition-colors ${
                          isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-slate-950'
                        }`}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${
                      isDarkMode
                        ? 'border-rose-400/20 bg-rose-500/10 text-rose-200'
                        : 'border-rose-200 bg-rose-50 text-rose-700'
                    }`}>
                      {error}
                    </div>
                  )}

                  <motion.button
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.99 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-[1.2rem] bg-gradient-to-r from-cyan-400 via-sky-500 to-blue-600 px-5 py-4 text-sm font-semibold text-slate-950 shadow-lg shadow-cyan-500/30 transition-opacity disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                    <ArrowRight className="h-4 w-4" />
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
